import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageCircle, KeyRound, Zap, Droplets } from "lucide-react";
import { TELEFONO, TELEFONO_HREF } from "@/data/citta";
import { vaiA } from "@/lib/scroll";

const HERO_IMG =
  "https://images.unsplash.com/photo-1610016302534-6f67f1c968d8?crop=entropy&cs=srgb&fm=jpg&q=85";

function Riga({ children, delay = 0 }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 200]);
  const opacity = useTransform(scrollY, [0, 600], [1, 0.3]);

  return (
    <section id="top" className="relative flex min-h-screen items-center overflow-hidden">
      <motion.div style={{ y, opacity }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Tecnico di pronto intervento al lavoro"
          className="h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/70 via-obsidian/60 to-obsidian" />
        <div className="absolute left-1/2 top-1/3 h-96 w-96 -translate-x-1/2 rounded-full bg-amber-500/10 blur-[120px]" />
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-5xl px-5 pb-24 pt-36 text-center md:px-8 md:pt-40">
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-2"
          data-testid="hero-badge-operatori"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
          </span>
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-emerald-400">
            Operatori attivi ora · H24/7
          </span>
        </motion.div>

        <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white text-balance sm:text-5xl lg:text-7xl">
          <Riga delay={0.15}>Guasto improvviso?</Riga>
          <Riga delay={0.3}>
            <span className="text-amber-400">Arriviamo noi.</span>
          </Riga>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mx-auto mt-7 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
        >
          Idraulici, elettricisti, fabbri e spurghisti attivi 24 ore su 24 in tutta la
          Lombardia. Una sola chiamata, un tecnico certificato da te, giorno e notte.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-4"
        >
          <a
            href={TELEFONO_HREF}
            data-testid="cta-chiama-ora-hero"
            className="inline-flex items-center gap-2.5 rounded-full bg-amber-500 px-8 py-4 text-base font-bold text-obsidian transition-all hover:-translate-y-0.5 hover:bg-amber-400 hover:shadow-[0_12px_40px_rgba(245,158,11,0.35)]"
          >
            <Phone size={19} strokeWidth={2.5} />
            Chiama ora · {TELEFONO}
          </a>
          <button
            type="button"
            data-testid="cta-chatta-con-noi"
            onClick={() => vaiA("#chat")}
            className="inline-flex items-center gap-2.5 rounded-full border border-slate-600 px-8 py-4 text-base font-semibold text-white transition-all hover:-translate-y-0.5 hover:border-amber-400 hover:text-amber-400"
          >
            <MessageCircle size={19} />
            Chatta con noi
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="mx-auto mt-16 grid max-w-2xl grid-cols-3 gap-6 border-t border-slate-800 pt-8"
        >
          {[
            { icon: KeyRound, valore: "Fabbro", label: "Apertura porte e serrature" },
            { icon: Zap, valore: "Elettricista", label: "Guasti e cortocircuiti" },
            { icon: Droplets, valore: "Idraulico", label: "Perdite e allagamenti" },
          ].map((s) => (
            <div key={s.valore} data-testid={`hero-stat-${s.valore.toLowerCase()}`} className="text-center">
              <s.icon size={20} className="mx-auto mb-2 text-amber-400" />
              <p className="font-display text-xl font-bold text-white sm:text-2xl">{s.valore}</p>
              <p className="mt-1 text-xs text-slate-400 sm:text-sm">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
